import type { HttpError } from '@refinedev/core'
import { CanAccess } from '@refinedev/core'
import { useTable } from '@refinedev/react-table'
import type { Cabinet, Round } from '@sirius/contracts/admin'
import type { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { ListEmptyState } from '~/components/listEmptyState'
import { DataTable } from '~/components/refineUi/dataTable/dataTable'
import { DataTableFilterDropdownDateRangePicker } from '~/components/refineUi/dataTable/dataTableFilter'
import { DataTableSorter } from '~/components/refineUi/dataTable/dataTableSorter'
import { Button } from '~/components/ui/button'
import { formatCredits, formatDateTime, formatNumber } from '~/i18n/format'
import { formatDateRange } from '~/pages/activity/activityFormat'
import type { GameNames } from '~/pages/cabinets/gameNames'
import { ROUNDS_RESOURCE } from '~/providers/dataProvider'
import { roundPath, stakeText } from './roundFormat'
import { RoundStatusBadge } from './roundStatusBadge'

type CabinetRoundsProps = {
  cabinet: Pick<Cabinet, 'id'>
  /** the name of each round's game */
  gameNames: GameNames
}

/**
 * A cabinet's rounds on its show page, newest first: one row per paid spin
 * (its free games, gamble and collect are part of it), with what it cost and
 * paid out. Only for staff and the host tenant's owners/admins: nothing for
 * members (a hidden marker once access is denied, so e2e can tell the denial
 * from an answer still on its way).
 */
export function CabinetRounds(props: CabinetRoundsProps) {
  const { t } = useTranslation()
  return (
    <CanAccess
      resource={ROUNDS_RESOURCE}
      action="list"
      fallback={
        <span
          hidden
          data-testid="rounds-denied"
        />
      }
    >
      <section
        aria-labelledby="rounds-heading"
        className="space-y-3"
      >
        <div className="space-y-1">
          <h3
            id="rounds-heading"
            className="font-semibold"
          >
            {t('roundsPage.list.heading')}
          </h3>
          <p className="text-muted-foreground max-w-2xl text-sm">
            {t('roundsPage.list.intro')}
          </p>
        </div>
        <RoundTable {...props} />
      </section>
    </CanAccess>
  )
}

function RoundTable({ cabinet, gameNames }: CabinetRoundsProps) {
  const { t } = useTranslation()
  const columns = useMemo<ColumnDef<Round>[]>(
    () => [
      {
        id: 'openedAt',
        accessorKey: 'openedAt',
        size: 200,
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            {t('roundsPage.list.opened')}
            <DataTableSorter column={column} />
            <DataTableFilterDropdownDateRangePicker
              column={column}
              defaultOperator="between"
              formatDateRange={formatDateRange}
            />
          </div>
        ),
        cell: ({ row }) => (
          <span className="whitespace-normal">
            {formatDateTime(row.original.openedAt)}
          </span>
        ),
        meta: { filterOperator: 'between' },
      },
      {
        id: 'gameId',
        accessorKey: 'gameId',
        header: t('roundsPage.list.game'),
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) =>
          gameNames.isLoading
            ? '…'
            : (gameNames.byId.get(row.original.gameId) ?? '—'),
      },
      {
        id: 'stakeTotal',
        accessorKey: 'stakeTotal',
        header: t('roundsPage.list.stake'),
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span
            className="tabular-nums"
            title={stakeText(row.original.stake, t)}
          >
            {formatCredits(row.original.stakeTotal)}
          </span>
        ),
      },
      {
        id: 'totalWin',
        accessorKey: 'totalWin',
        header: t('roundsPage.list.win'),
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="tabular-nums">
            {formatCredits(row.original.totalWin)}
          </span>
        ),
      },
      {
        id: 'freeGames',
        accessorKey: 'freeGames',
        header: t('roundsPage.list.freeGames'),
        size: 110,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="tabular-nums">
            {formatNumber(row.original.freeGames)}
          </span>
        ),
      },
      {
        id: 'status',
        accessorKey: 'closedAt',
        header: t('roundsPage.list.status'),
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => <RoundStatusBadge round={row.original} />,
      },
      {
        id: 'actions',
        header: '',
        size: 90,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              asChild
            >
              <Link
                to={roundPath(row.original)}
                aria-label={t('roundsPage.list.viewAria', {
                  date: formatDateTime(row.original.openedAt),
                })}
              >
                {t('roundsPage.list.view')}
              </Link>
            </Button>
          </div>
        ),
      },
    ],
    [gameNames, t],
  )

  const table = useTable<Round, HttpError>({
    columns,
    refineCoreProps: {
      resource: ROUNDS_RESOURCE,
      // the show page's URL belongs to the cabinet, not to this table
      syncWithLocation: false,
      pagination: { pageSize: 10 },
      sorters: { initial: [{ field: 'openedAt', order: 'desc' }] },
      filters: {
        permanent: [{ field: 'cabinetId', operator: 'eq', value: cabinet.id }],
      },
    },
  })
  const { tableQuery, filters } = table.refineCore
  // the permanent cabinet filter is not one the user can clear
  const hasFilters = filters.some(
    (filter) => 'field' in filter && filter.field !== 'cabinetId',
  )

  if (tableQuery.isError) {
    return (
      <p
        role="alert"
        className="text-destructive text-sm"
      >
        {t('roundsPage.list.loadError', { message: tableQuery.error.message })}
      </p>
    )
  }
  return tableQuery.data?.total === 0 ? (
    <ListEmptyState
      resource={ROUNDS_RESOURCE}
      emptyText={t('roundsPage.list.empty')}
      hasFilters={hasFilters}
      onClearFilters={() => table.reactTable.setColumnFilters([])}
      canCreate={false}
      noMatchText={t('roundsPage.list.noMatch')}
    />
  ) : (
    <DataTable
      table={table}
      aria-label={t('roundsPage.list.tableAria')}
    />
  )
}
